import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ViewerActions } from '@app/client/viewer/actions';
import * as fromRoot from '@app/reducers';
import { SmashPlayerComponent } from '@app/shared/components/smash-player/smash-player.component';
import { Device } from '@app/shared/models/device';
import { TransferFile } from '@app/shared/models/transferFile';
import { Store } from '@ngrx/store';
import Plyr, { Options, SourceInfo } from 'plyr';
import * as fromViewer from '../../reducers/viewer.reducer';
import { isInThePast } from 'smash-time';
import { GetTransferPreviewOutput } from '@smash-sdk/transfer/ejs/01-2024';
import { Constant } from 'src/constant';

@Component({
  selector: 'app-modal-preview',
  templateUrl: './modal-preview.component.html',
  styleUrls: ['./modal-preview.component.scss'],
})
export class ModalPreviewComponent implements OnInit, OnChanges {

  @Input() preview: GetTransferPreviewOutput;
  @Input() file: TransferFile;
  @Input() initialFileOffset: number;
  @Input() files: TransferFile[];
  @Input() device: Device;

  selectedFile: TransferFile;
  playerSource: SourceInfo;
  playerOptions: Options;
  allowTouchMove: boolean;
  filesReady = false;
  initialFile: TransferFile;
  counter = 0
  previewable: boolean;

  @ViewChild('player') player: SmashPlayerComponent;
  @Output() onClose: EventEmitter<{ fileId: string, fileOffset: number }> = new EventEmitter<{ fileId: string, fileOffset: number }>();

  constructor(
    private store$: Store<fromRoot.State & fromViewer.State>,
  ) { }

  ngOnInit(): void {
    this.setSelectedFile(this.file);
    this.loadMoreFilesIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.files) {
      if (this.selectedFile) {
        this.selectedFile = this.files.find(file => file.index === this.selectedFile.index);
        this.previewable = this.selectedFile.previewable;
        if (this.selectedFile.type === 'audio' || this.selectedFile.type === 'video') {
          this.setupPlayer();
        }
      }
    }
  }

  handleImageError() {
    // fallback when preview has failed
    this.previewable = false;
  }

  /* Loading more files when opening preview modal on last loaded files (slider event 'onReachEnd' not triggered on init) */
  loadMoreFilesIfNeeded(): void {
    if (this.file.index + 1 === this.files.length && this.files.length < this.preview.transfer.filesNumber) {
      this.store$.dispatch(ViewerActions.LoadMoreFiles());
    }
  }

  downloadFile(file: TransferFile): void {
    this.store$.dispatch(ViewerActions.RequestDownloadUrls({ file }));
  }

  onReachEnd(): void {
    if (this.files.length < this.preview.transfer.filesNumber) {
      this.store$.dispatch(ViewerActions.LoadMoreFiles());
    }
  }

  closeModal(): void {
    this.onClose.emit({ fileId: this.initialFile.id, fileOffset: this.initialFileOffset });
    this.store$.dispatch(ViewerActions.CloseModalPreview());
  }

  setSelectedFile(file: TransferFile): void {
    this.previewable = file.previewable;
    this.initialFile = file;
    this.selectedFile = file;
    if (this.selectedFile) {
      if (this.selectedFile.type === 'audio' || this.selectedFile.type === 'video') {
        this.setupPlayer();
      }
    }
  }

  onIndexChange($event: Event & { index: number }) {
    this.selectedFile = this.files.find(file => file.index === $event.index);
    this.previewable = this.selectedFile.previewable;
    if (this.selectedFile?.expiry && isInThePast(this.selectedFile?.expiry)) {
      this.store$.dispatch(ViewerActions.RefreshFileAndAssociatedFiles({ file: this.selectedFile }));
    } else {
      if (this.selectedFile?.previewable && (this.selectedFile?.type === 'audio' || this.selectedFile?.type === 'video')) {
        this.setupPlayer();
      }
    }
  }

  lockNavigation(): void {
    this.allowTouchMove = false;
  }

  unlockNavigation(): void {
    this.allowTouchMove = this.device.isMobile || this.device.isTablet;
  }

  // Section for handling players //

  setupPlayer(): void {
    this.playerSource = this.buildPlayerSource(this.selectedFile);
    this.playerOptions = this.buildPlayerOptions(this.selectedFile);
  }

  get playerIsPlaying(): boolean {
    if (!!this.player) {
      return !!this.player.isPlaying;
    }
    return false;
  }

  togglePlay(event: Event): void {
    if (this.player) {
      if (this.player.isPlaying) {
        this.player.pause();
      } else {
        this.player.play();
      }
      event.stopPropagation();
      event.preventDefault();
    }
  }

  buildPlayerSource(file: TransferFile): SourceInfo {
    const sources: SourceInfo = {
      sources: [
        {
          src: file.preview,
          provider: Constant.videoProviders[file.provider],
        }
      ],
      type: file.type as Plyr.MediaType,
    }
    return sources;
  }

  buildPlayerOptions(file: TransferFile): Options {
    const options: Options = {
      volume: 100,
      disableContextMenu: true,
      storage: { enabled: false }
    };

    if (file.type === 'audio') {
      options.controls = ['play', 'progress', 'current-time', 'invert-time'];
      options.autoplay = false;
    }

    if (file.type === 'video') {
      options.controls = ['play', 'progress', 'volume', 'speed', 'duration', 'mute', 'settings', 'fullscreen'];
      options.fullscreen = { enabled: true, fallback: true, iosNative: this.device.deviceInfo.os === 'iOS' || this.device.deviceInfo.os === 'Mac', container: null }
    }

    return options;
  }

}
